import { useMemo, useRef } from "react";
import MsTable from "../../../components/Widgets/Table";
import KlydoModal from "./KlydoModal";
import { Klydo } from "../../../api/klydo";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import { useOnScreen } from "../../../components/hooks/useOnScreen";
import useGetKlydosByIds from "../../Device/hooks/useGetKlydosByIds";
import orderKlydosByIds from "../utils/orderKlydosByIds";

// A round, lazily-played thumbnail — mirrors the one used on the main klydos
// table so rows look identical here.
const GifThumbnail = ({ loopUrl }: { loopUrl?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(containerRef);

  if (!loopUrl) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>File type not supported</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        borderRadius: "50%",
        width: 96,
        height: 96,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          aspectRatio: "1/1",
        }}
      >
        {isOnScreen && (
          <video
            muted
            src={loopUrl}
            loop
            autoPlay
            style={{ width: "auto", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
    </div>
  );
};

const tableColumns = (editable: boolean) => [
  { sort: false, field: "index", label: "#", size: editable ? 90 : 60 },
  { sort: true, field: "id", label: "id", size: 240 },
  { sort: true, field: "authorName", label: "author", size: 200 },
  { sort: true, field: "name", label: "name", size: 200 },
  { sort: false, field: "image", label: "gif" },
  { sort: true, field: "createdAt", label: "date", size: 220 },
];

// A small position box, mirroring the index cell on the greet categories table:
// type a new position and the klydo moves there, the rest shifting up/down.
// Commits on blur/Enter (each commit is a PUT, so not on every keystroke).
const IndexInput = ({
  position,
  total,
  onMove,
}: {
  position: number;
  total: number;
  onMove: (newPosition: number) => void;
}) => {
  const commit = (raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.min(Math.max(Math.round(parsed), 1), total);
    if (clamped === position) return;
    onMove(clamped);
  };

  return (
    <DSTextField
      // Re-read defaultValue once the row lands on a new position.
      key={`pos-${position}`}
      size="small"
      defaultValue={position}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      sx={{ width: "72px" }}
    />
  );
};

const buildRow = (
  r: Klydo,
  position: number,
  total: number,
  onMove?: (klydoId: string, newPosition: number) => void,
) => ({
  index: onMove ? (
    <IndexInput
      position={position}
      total={total}
      onMove={(newPosition) => onMove(r.id, newPosition)}
    />
  ) : (
    <p style={{ paddingRight: "8px" }}>{position}</p>
  ),
  id: <p>{r.id}</p>,
  image: <GifThumbnail loopUrl={r.klydoAnimation?.xs ?? r.loopUrl} />,
  authorName: <p title={r.creator}>{r.author?.name}</p>,
  name: r.name,
  createdAt: r.createdAt ? new Date(r.createdAt).toLocaleString("en-GB") : "",
  key: r.id,
});

type KlydosByIdsTableProps = {
  klydosIds: string[];
  /** Used to give the underlying table a stable, unique id. */
  tableId?: string;
  /** When set, the "#" cell becomes an editable position box and this is
   * called with the reordered ids. Omit for a read-only table. */
  onReorder?: (klydosIds: string[]) => void;
};

/**
 * Renders a klydos table (identical look & behaviour to the main klydos table —
 * clicking a row opens the KlydoModal) for an explicit list of klydo ids.
 */
const KlydosByIdsTable = ({
  klydosIds,
  tableId = "firmware-klydos",
  onReorder,
}: KlydosByIdsTableProps) => {
  const { klydos, isLoading } = useGetKlydosByIds(klydosIds);
  // Sorting is off (cancelSort), so rows render in data order — keep that the
  // caller's id order, which is the order the firmware/XML export uses.
  const orderedKlydos = useMemo(
    () => orderKlydosByIds(klydos, klydosIds),
    [klydos, klydosIds],
  );

  const handleMove = (klydoId: string, newPosition: number) => {
    const from = klydosIds.indexOf(klydoId);
    if (from === -1) return;
    const next = klydosIds.slice();
    next.splice(from, 1);
    next.splice(newPosition - 1, 0, klydoId);
    onReorder?.(next);
  };

  if (klydosIds.length === 0) {
    return (
      <DSBox sx={{ textAlign: "center", p: 4 }}>
        <DSTypography color="text.secondary">
          No klydos in this firmware.
        </DSTypography>
      </DSBox>
    );
  }

  if (isLoading) {
    return <DSCircularProgress />;
  }

  return (
    <MsTable
      id={tableId}
      cancelSort={true}
      actions={() => <></>}
      // The position comes from klydosIds, not the row index — searching filters
      // the rows, and a filtered row index is not the klydo's real position.
      rowBuild={(r: Klydo) =>
        buildRow(
          r,
          klydosIds.indexOf(r.id) + 1,
          klydosIds.length,
          onReorder && handleMove,
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search={(row: any, val: string) =>
        row.id?.toLowerCase().includes(val) ||
        row.author?.name?.toLowerCase().includes(val) ||
        row.name?.toLowerCase().includes(val)
      }
      data={orderedKlydos}
      initSort={"createdAt"}
      cols={tableColumns(!!onReorder)}
      title=""
      modal={KlydoModal}
    />
  );
};

export default KlydosByIdsTable;
